import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import {
  Banner,
  Button,
  Checkbox,
  ErrorState,
  PageHead,
  Skeleton,
  TextField,
  focusFirstInvalid,
  useToast,
} from "@ui/react";
import { AlertIcon, ChevronLeftIcon, PlusIcon, TrashIcon } from "@ui/react/icons";
import {
  deleteAvailabilityOverride,
  getDirectBookingSettings,
  listAvailabilityOverrides,
  updateWeeklyAvailability,
  upsertAvailabilityOverride,
} from "../../lib/api/contractor";
import type { AvailabilityDay, AvailabilityOverride } from "../../lib/types/models";
import { dateOnly, isValidTime, minutesFromTime, todayInSAST } from "../../lib/format";
import { errorMessage } from "../../lib/errorMessage";
import { ApiError } from "../../lib/api/client";

/** The API takes exactly seven days, Monday first — index is the contract. */
const DAY_NAMES = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

function emptyWeek(): AvailabilityDay[] {
  return DAY_NAMES.map(() => ({ enabled: false, windows: [] }));
}

/** Pads or trims whatever came back to the seven the PUT insists on. */
function normalizeWeek(days: AvailabilityDay[] | undefined): AvailabilityDay[] {
  const base = emptyWeek();
  if (!days) return base;
  return base.map((fallback, index) => {
    const day = days[index];
    if (!day) return fallback;
    return { enabled: day.enabled, windows: day.windows ? [...day.windows] : [] };
  });
}

/**
 * Everything the server checks, checked here first. A round trip to a
 * cold-starting Cloud Run instance is a slow way to learn that 17:00 comes
 * before 09:00, and the 400 it answers names no day.
 */
function validateWeek(days: AvailabilityDay[]): Record<string, string> {
  const errors: Record<string, string> = {};

  days.forEach((day, dayIndex) => {
    const name = DAY_NAMES[dayIndex] ?? `Day ${dayIndex + 1}`;

    if (day.enabled && day.windows.length === 0) {
      errors[`day-${dayIndex}`] = `Add hours for ${name}, or switch the day off.`;
    }

    const spans: { open: number; close: number }[] = [];

    day.windows.forEach((window, windowIndex) => {
      const openOk = isValidTime(window.open);
      const closeOk = isValidTime(window.close);

      if (!openOk) errors[`day-${dayIndex}-window-${windowIndex}-open`] = "Use a time like 08:00.";
      if (!closeOk) errors[`day-${dayIndex}-window-${windowIndex}-close`] = "Use a time like 17:00.";
      if (!openOk || !closeOk) return;

      const open = minutesFromTime(window.open);
      const close = minutesFromTime(window.close);
      if (close <= open) {
        errors[`day-${dayIndex}-window-${windowIndex}-close`] =
          "The closing time must be after the opening time.";
        return;
      }

      // Windows within a day must be disjoint; the server rejects the whole
      // week if any pair touches.
      if (spans.some((span) => open < span.close && span.open < close)) {
        errors[`day-${dayIndex}-window-${windowIndex}-open`] =
          "These hours overlap another window on this day.";
        return;
      }
      spans.push({ open, close });
    });
  });

  return errors;
}

export function AvailabilityScreen() {
  const toast = useToast();
  const formRef = useRef<HTMLFormElement>(null);

  const [state, setState] = useState<{ loading: boolean; error: unknown }>({
    loading: true,
    error: null,
  });
  const [reloadKey, setReloadKey] = useState(0);
  const [days, setDays] = useState<AvailabilityDay[]>(emptyWeek);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    let ignore = false;
    setState({ loading: true, error: null });

    getDirectBookingSettings()
      .then((settings) => {
        if (ignore) return;
        setDays(normalizeWeek(settings.weeklyAvailability?.days));
        setState({ loading: false, error: null });
      })
      .catch((error: unknown) => {
        if (!ignore) setState({ loading: false, error });
      });

    return () => {
      ignore = true;
    };
  }, [reloadKey]);

  const patchDay = useCallback((index: number, patch: Partial<AvailabilityDay>) => {
    setDays((current) => current.map((day, i) => (i === index ? { ...day, ...patch } : day)));
  }, []);

  const onSave = async () => {
    const found = validateWeek(days);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      focusFirstInvalid(formRef.current);
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      const result = await updateWeeklyAvailability({ days });
      setDays(normalizeWeek(result.days));
      // The submit sits at the bottom of a seven-card page; a banner above the
      // head would confirm the save off-screen.
      toast.show("Weekly hours saved.");
    } catch (error) {
      setSaveError(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };


  return (
    <div className="stack stack--loose">
      {/* No eyebrow: the back link below names this same section, and a head
          that says "Booking setup" twice in adjacent lines is stammering. See
          PageHead for the rule. */}
      <PageHead
        title="Availability"
        back={
          <Link className="back-link" to="/settings/booking">
            <ChevronLeftIcon />
            Booking setup
          </Link>
        }
      />

      {state.loading ? (
        <div role="status">
          <span className="sr-only">Loading your weekly hours</span>
          <Skeleton height="26rem" />
        </div>
      ) : state.error ? (
        <ErrorState message={errorMessage(state.error)} onRetry={() => setReloadKey((key) => key + 1)} />
      ) : (
        <>
          {saveError ? (
            <Banner tone="danger" title="We couldn't save your hours">
              {saveError}
            </Banner>
          ) : null}

          <form
            ref={formRef}
            className="form"
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              void onSave();
            }}
          >
            <div className="stack">
              <h2 className="headline-sm">Weekly hours</h2>
              <p className="muted">
                Customers can only pick times inside these hours. Times are in your booking
                timezone, set under Booking rules.
              </p>

              {days.map((day, dayIndex) => {
                const name = DAY_NAMES[dayIndex] ?? `Day ${dayIndex + 1}`;
                const dayError = errors[`day-${dayIndex}`];
                const addHoursId = `add-hours-${dayIndex}`;
                return (
                  <div key={name} className="card card--dense">
                    <div className="stack">
                      <div className="cluster cluster--between">
                        <span className="headline-sm">{name}</span>
                        <Checkbox
                          label={`Open on ${name}`}
                          checked={day.enabled}
                          onChange={(event) => {
                            const enabled = event.target.checked;
                            patchDay(dayIndex, {
                              enabled,
                              // An open day with no window is a state this form
                              // then rejects, so opening one seeds a normal day
                              // instead of creating work.
                              windows:
                                enabled && day.windows.length === 0
                                  ? [{ open: "09:00", close: "17:00" }]
                                  : day.windows,
                            });
                          }}
                        />
                      </div>

                      {day.enabled ? (
                        <>
                          {dayError ? (
                            <p className="field__error">
                              <AlertIcon />
                              {dayError}
                            </p>
                          ) : null}

                          {day.windows.map((window, windowIndex) => (
                            <div
                              className="cluster cluster--end"
                              key={`${name}-window-${windowIndex}`}
                            >
                              <div className="field-grid hours-window">
                                <TextField
                                  label={windowIndex === 0 ? "Opens" : `Opens (block ${windowIndex + 1})`}
                                  type="time"
                                  value={window.open}
                                  error={
                                    errors[`day-${dayIndex}-window-${windowIndex}-open`] ?? null
                                  }
                                  onChange={(event) =>
                                    patchDay(dayIndex, {
                                      windows: day.windows.map((candidate, i) =>
                                        i === windowIndex
                                          ? { ...candidate, open: event.target.value }
                                          : candidate,
                                      ),
                                    })
                                  }
                                />
                                <TextField
                                  label={
                                    windowIndex === 0 ? "Closes" : `Closes (block ${windowIndex + 1})`
                                  }
                                  type="time"
                                  value={window.close}
                                  error={
                                    errors[`day-${dayIndex}-window-${windowIndex}-close`] ?? null
                                  }
                                  onChange={(event) =>
                                    patchDay(dayIndex, {
                                      windows: day.windows.map((candidate, i) =>
                                        i === windowIndex
                                          ? { ...candidate, close: event.target.value }
                                          : candidate,
                                      ),
                                    })
                                  }
                                />
                              </div>
                              <button
                                type="button"
                                className="icon-btn"
                                aria-label={`Remove ${name} hours block ${windowIndex + 1}`}
                                onClick={() => {
                                  patchDay(dayIndex, {
                                    windows: day.windows.filter((_, i) => i !== windowIndex),
                                  });
                                  // This button unmounts with its own row, and
                                  // focus would otherwise fall to <body>.
                                  requestAnimationFrame(() =>
                                    document.getElementById(addHoursId)?.focus(),
                                  );
                                }}
                              >
                                <TrashIcon />
                              </button>
                            </div>
                          ))}

                          <div className="cluster">
                            <Button
                              id={addHoursId}
                              variant="secondary"
                              size="sm"
                              onClick={() =>
                                patchDay(dayIndex, {
                                  windows: [...day.windows, { open: "09:00", close: "17:00" }],
                                })
                              }
                            >
                              <PlusIcon />
                              {day.windows.length === 0 ? `Add hours` : `Add another block`}
                            </Button>
                            {day.windows.length > 0 ? (
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() =>
                                  setDays((current) =>
                                    current.map((other) =>
                                      other.enabled
                                        ? // Deep-copied: shared window objects
                                          // would edit every day at once.
                                          { ...other, windows: day.windows.map((w) => ({ ...w })) }
                                        : other,
                                    ),
                                  )
                                }
                              >
                                Copy to all open days
                              </Button>
                            ) : null}
                          </div>
                        </>
                      ) : (
                        <span className="dim">Closed — nothing can be booked.</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="cluster">
              <Button type="submit" loading={saving}>
                Save weekly hours
              </Button>
            </div>
          </form>

          <DateOverrides />
        </>
      )}
    </div>
  );
}

/**
 * One-off closures. Net-new on the web: the mobile app never wired these, so a
 * contractor had no way to take a public holiday off short of editing the whole
 * week.
 */
function DateOverrides() {
  const toast = useToast();
  const [date, setDate] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dateError, setDateError] = useState<string | null>(null);
  const [overrides, setOverrides] = useState<AvailabilityOverride[]>([]);
  const [listError, setListError] = useState<string | null>(null);
  const [listUnsupported, setListUnsupported] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let ignore = false;
    setListLoading(true);
    listAvailabilityOverrides(todayInSAST())
      .then(({ overrides: closed }) => {
        if (ignore) return;
        setOverrides(
          closed.filter((override) => override.closed).sort((a, b) => a.date.localeCompare(b.date)),
        );
        setListError(null);
        setListUnsupported(false);
      })
      .catch((caught: unknown) => {
        if (ignore) return;
        // The GET endpoint ships slightly behind this screen (PKG-CAPI-VALIDATION-FILTERS).
        // Until it lands, fall back to the old honest copy instead of a raw 404.
        if (caught instanceof ApiError && caught.status === 404) {
          setListUnsupported(true);
          setListError("Shoku can't list the days you've already closed, so keep your own note of them.");
          return;
        }
        setListUnsupported(false);
        setListError(errorMessage(caught));
      })
      .finally(() => {
        if (!ignore) setListLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [reloadKey]);

  const run = async (action: "close" | "reopen", target: string = date) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(target)) {
      setDateError("Pick a date first.");
      return;
    }
    setDateError(null);
    setBusy(`${action}:${target}`);
    setError(null);
    try {
      if (action === "close") {
        await upsertAvailabilityOverride(target, {
          closed: true,
          windows: [],
          reason: reason.trim(),
        });
        toast.show(`${target} is now closed — nothing can be booked that day.`);
      } else {
        // 204, no body: success is the absence of an error.
        await deleteAvailabilityOverride(target);
        toast.show(`${target} is back on your normal weekly hours.`);
      }
      setReason("");
      setReloadKey((key) => key + 1);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="card card--dense">
      <div className="stack">
        <h2 className="headline-sm">Close a specific day</h2>
        <p className="muted">
          Take a single date out of your diary without touching your weekly hours — a public
          holiday, a family day, a job that runs long. Reopening puts the day back on your
          normal hours.
        </p>

        {error ? (
          <Banner tone="danger" title="That didn't work">
            {error}
          </Banner>
        ) : null}

        <div className="field-grid">
          <TextField
            label="Date"
            type="date"
            min={todayInSAST()}
            value={date}
            error={dateError}
            onChange={(event) => {
              setDate(event.target.value);
              setDateError(null);
            }}
          />
          <TextField
            label="Reason"
            value={reason}
            hint="Optional, for your own records."
            onChange={(event) => setReason(event.target.value)}
          />
        </div>

        <div className="cluster">
          <Button
            variant="secondary"
            loading={busy === `close:${date}`}
            disabled={!date || busy !== null}
            onClick={() => void run("close")}
          >
            Close this day
          </Button>
        </div>

        <div className="stack">
          <h3 className="headline-sm">Closed days</h3>
          {listLoading ? (
            <p className="dim">Loading…</p>
          ) : listError ? (
            <p className={listUnsupported ? "dim" : "field__error"}>{listError}</p>
          ) : overrides.length === 0 ? (
            <p className="dim">No closed days on your diary right now.</p>
          ) : (
            <ul className="stack stack--tight">
              {overrides.map((override) => (
                <li key={override.date} className="cluster cluster--between">
                  <span>
                    {dateOnly(override.date)}
                    {override.reason ? ` — ${override.reason}` : ""}
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={busy === `reopen:${override.date}`}
                    disabled={busy !== null}
                    onClick={() => void run("reopen", override.date)}
                  >
                    Reopen
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
